import { useState } from 'react';
import { PetProfile, PetType } from '../types';
import { storage } from '../services/storageService';

interface UsePetManagementProps {
  currentUser: any;
  pets: PetProfile[];
  setPets: (pets: PetProfile[] | ((prev: PetProfile[]) => PetProfile[])) => void;
  setCurrentPetId: (id: string | null) => void;
  setIsEditProfileModalOpen: (open: boolean) => void;
  setIsPetSelectorOpen: (open: boolean) => void;
  setStatusMessage: (message: { type: 'success' | 'error', text: string } | null) => void;
  setIsSaving: (saving: boolean) => void;
  subscription: any;
  canPerformAction: (action: 'addPet' | 'addLog' | 'useAiChat' | 'addReminder') => Promise<{ allowed: boolean; reason?: string; currentUsage?: number; limit?: number }>;
  checkAndShowLimitBanners: () => void;
  profile: PetProfile | null;
  loadPetSpecificData: (petId: string) => void;
  updateUsage: (usage: any) => Promise<any>;
  currentPetId: string | null;
}

export const usePetManagement = ({
  currentUser,
  pets,
  setPets,
  setCurrentPetId,
  setIsEditProfileModalOpen,
  setIsPetSelectorOpen,
  setStatusMessage,
  setIsSaving,
  subscription,
  canPerformAction,
  checkAndShowLimitBanners,
  profile,
  loadPetSpecificData,
  updateUsage,
  currentPetId
}: UsePetManagementProps) => {
  const [petForm, setPetForm] = useState<Partial<PetProfile>>({
    name: '',
    type: 'chien',
    breed: '',
    color: '',
    identificationNumber: '',
    birthDate: new Date().toISOString().split('T')[0],
    weight: 0,
    imageUrl: ''
  });

  const handleOpenAddPet = () => {
    setPetForm({
      name: '',
      type: 'chien',
      breed: '',
      color: '',
      identificationNumber: '',
      birthDate: new Date().toISOString().split('T')[0],
      weight: 0,
      imageUrl: ''
    });
    setIsEditProfileModalOpen(true);
    setIsPetSelectorOpen(false);
  };

  const handleOpenEditPet = () => {
    if (!currentPetId) return;
    const currentProfile = pets.find(p => p.id === currentPetId);
    if (!currentProfile) return;
    setPetForm({ ...currentProfile });
    setIsEditProfileModalOpen(true);
    setIsPetSelectorOpen(false);
  };

  const handleSavePetProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    
    setIsSaving(true);
    try {
      const isNewPet = !(petForm as any).id;
      
      if (isNewPet) {
        const permission = await canPerformAction('addPet');
        if (!permission.allowed) {
          setStatusMessage({ type: 'error', text: permission.reason || 'Limite d\'animaux atteinte' });
          return;
        }
      }

      // Vérifier que l'utilisateur est connecté
      if (!currentUser?.uid) {
        setStatusMessage({ type: 'error', text: 'Utilisateur non connecté' });
        return;
      }

      // Ajouter le userId avant de sauvegarder
      const petToSave = { ...petForm, userId: currentUser.uid } as PetProfile;
      const savedId = await storage.savePet(petToSave);
      
      if (isNewPet) {
        const newPet = { id: savedId, ...petForm, userId: currentUser.uid } as PetProfile;
        setPets(prev => [...prev, newPet]);
        setCurrentPetId(newPet.id);
        
        // Mettre à jour le compteur d'utilisation
        await updateUsage({
          currentPets: (subscription?.usage?.currentPets ?? 0) + 1
        });
        
        loadPetSpecificData(newPet.id);
      } else {
        // Pour un animal existant, on met à jour l'animal existant avec les nouvelles données
        setPets(prev => prev.map(p => p.id === savedId ? { ...p, ...petForm } : p));
      }

      setIsEditProfileModalOpen(false);
      setIsPetSelectorOpen(false);
      setStatusMessage({ type: 'success', text: 'Profil enregistré' });
      
      // Vérifier les limites après l'ajout
      checkAndShowLimitBanners();
    } catch (e) {
      console.error(e);
      const err = e as any;
      const msg = err?.message ?? String(e);
      if (msg.includes('missing_user_id')) {
        setStatusMessage({ type: 'error', text: 'Erreur: userId manquant lors de la sauvegarde (rechargez la page et reconnectez-vous).' });
      } else {
        setStatusMessage({ type: 'error', text: 'Erreur sauvegarde' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePet = async (petId: string) => {
    if (!currentUser) return;
    
    // Confirmer la suppression
    if (!confirm('Êtes-vous sûr de vouloir supprimer cet animal ? Cette action est irréversible.')) {
      return;
    }
    
    setIsSaving(true);
    try {
      // Supprimer l'animal
      await storage.deletePet(petId);
      
      // Mettre à jour la liste des animaux
      const updatedPets = pets.filter(p => p.id !== petId);
      setPets(updatedPets);
      
      // Si c'était l'animal courant, sélectionner le premier animal restant ou ouvrir la modal d'ajout
      if (currentPetId === petId) {
        if (updatedPets.length > 0) {
          setCurrentPetId(updatedPets[0].id);
        } else {
          setCurrentPetId(null);
          setIsEditProfileModalOpen(true);
        }
      }
      
      // Mettre à jour le compteur d'utilisation
      if (subscription && subscription.usage.currentPets > 0) {
        await updateUsage({
          currentPets: subscription.usage.currentPets - 1
        });
      }
      
      // Vérifier les limites après la suppression
      checkAndShowLimitBanners();
      
      setStatusMessage({ type: 'success', text: 'Animal supprimé' });
    } catch (e) {
      console.error('Error deleting pet:', e);
      setStatusMessage({ type: 'error', text: 'Erreur suppression' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSeedData = async () => {
    if (!profile || !currentUser) return;
    setIsSaving(true);
    setStatusMessage({ type: 'success', text: 'Génération en cours...' });

    try {
      await storage.deleteAllLogs(profile.id);

      const updatedProfile = {
        ...profile,
        name: "Rocky",
        breed: "Boxer",
        type: 'chien' as PetType,
        color: 'Fauve',
        identificationNumber: '25026...',
        weight: 37.0
      };
      await storage.savePet(updatedProfile);
      setPets(prev => prev.map(p => p.id === updatedProfile.id ? updatedProfile : p));

      const today = new Date();
      const promises = [];

      for (let i = 29; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        d.setHours(8 + Math.floor(Math.random() * 12), Math.floor(Math.random() * 60));
        const weight = 37 + Math.sin(i * 0.5) * 0.8 + (Math.random() * 0.4 - 0.2);
        const hasDigestionIssue = Math.random() > 0.5;
        const isSickDay = i === 5 || i === 4;

        let stoolScore = 2, stoolColor = 'brown', notes = "Tout va bien.";
        if (isSickDay) {
           stoolScore = 6; stoolColor = 'yellow_gray'; notes = "Malade, diarrhée.";
        } else if (hasDigestionIssue) {
           stoolScore = Math.floor(Math.random() * 3) + 3; 
           stoolColor = Math.random() > 0.6 ? 'orange' : 'green';
           notes = "Selles molles.";
        }

        const log: any = {
          petId: profile.id,
          date: d.toISOString(),
          weight: Number(weight.toFixed(2)),
          mood: isSickDay ? 'léthargique' : 'joyeux',
          appetite: isSickDay ? 'faible' : 'excellent',
          stoolScore, stoolColor, symptoms: isSickDay ? ['Diarrhée'] : [], notes
        };
        promises.push(storage.saveLog(log));
      }

      await Promise.all(promises);
      loadPetSpecificData(profile.id);
      setStatusMessage({ type: 'success', text: 'Données générées !' });
    } catch (e) {
      console.error(e);
      setStatusMessage({ type: 'error', text: 'Erreur génération' });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    petForm,
    setPetForm,
    handleOpenAddPet,
    handleOpenEditPet,
    handleSavePetProfile,
    handleDeletePet,
    handleSeedData
  };
};
